import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_EQUAL_SIGN } from "const";
import { getFilterChangeDelay } from "../filterChangeDelay";

const normalize = (value, isSuppliersTrunks) => {
    if (isSuppliersTrunks) return value;
    const date = value.start_date;
    return {
        ...value,
        start_date: date
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
            : undefined,
        start_date_inequation: date ? value.start_date_inequation : undefined,
    };
};

export const useNumbersFilterForm = ({
    filters, onChange, proxy, model, isSuppliersTrunks, currentRangeNumber, currentDisplay,
}) => {
    // Keep the editable draft separate from the parent's last applied API filters.
    const [formValue, setFormValue] = useState(() => ({
        show_allocated_numbers: true,
        show_unallocated_numbers: true,
        show_test_numbers: true,
        show_block_allocation_numbers: true,
        ...filters,
        actual: filters && typeof filters.actual === "boolean" ? !filters.actual : false,
        str: currentRangeNumber || (filters && filters.str) || "",
        start_date: filters && filters.start_date
            ? new Date(filters.start_date instanceof Date ? filters.start_date : `${filters.start_date}T00:00:00`)
            : null,
        start_date_inequation: (filters && filters.start_date_inequation) ?? DEFAULT_EQUAL_SIGN,
    }));
    const valueRef = useRef(formValue);
    const timerRef = useRef(null);
    const callbacksRef = useRef({ onChange, proxy });
    const lastEmittedRef = useRef(null);
    const previousRangeRef = useRef(currentRangeNumber);

    useEffect(() => { callbacksRef.current = { onChange, proxy }; }, [onChange, proxy]);

    // A different display or external search must not receive a stale pending query.
    useEffect(() => {
        clearTimeout(timerRef.current);
        lastEmittedRef.current = null;
        return () => clearTimeout(timerRef.current);
    }, [currentDisplay, currentRangeNumber, isSuppliersTrunks]);

    useEffect(() => {
        if (previousRangeRef.current === currentRangeNumber) return;
        previousRangeRef.current = currentRangeNumber;
        const next = { ...valueRef.current, str: currentRangeNumber || "" };
        valueRef.current = next;
        setFormValue(next);
    }, [currentRangeNumber]);

    const getValues = useCallback(() => {
        const value = normalize(valueRef.current, isSuppliersTrunks);
        const { proxy: transform } = callbacksRef.current;
        return transform ? transform(value) : value;
    }, [isSuppliersTrunks]);

    const handleChange = useCallback(nextValue => {
        const previous = valueRef.current;
        const next = {
            ...nextValue,
            start_date_inequation: nextValue.start_date
                ? nextValue.start_date_inequation ?? DEFAULT_EQUAL_SIGN
                : DEFAULT_EQUAL_SIGN,
        };
        valueRef.current = next;
        setFormValue(next);
        clearTimeout(timerRef.current);

        if (model && Object.values(model.check(next)).some(result => result.hasError)) return;

        const emit = () => {
            const value = getValues();
            const serialized = JSON.stringify(value);
            if (serialized === lastEmittedRef.current) return;
            lastEmittedRef.current = serialized;
            callbacksRef.current.onChange(value);
        };
        const delay = getFilterChangeDelay(next, previous);
        if (delay) timerRef.current = setTimeout(emit, delay);
        else emit();
    }, [getValues, model]);

    return { formValue, handleChange, getValues };
};
